import { useContext, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { AuthContext } from "../../../Context/AuthContext";
import {
  BUSINESS_CATEGORIES,
  BUSINESS_TYPES,
  FIELD_LIMITS,
  NIGERIAN_STATES,
  EMPTY_BUSINESS_FORM,
  merchantToForm,
  buildBusinessSavePayload,
} from "../utils/merchantBusinessFormConfig";

const CharCount = ({ value, max }) => (
  <span className="dashboard-char-count">
    {(value || "").length}/{max}
  </span>
);

const Field = ({ label, children, hint, count, max, full }) => (
  <label className={`dashboard-form-field${full ? " dashboard-form-field--full" : ""}`}>
    <span>
      {label}
      {count != null && max != null ? <CharCount value={count} max={max} /> : null}
    </span>
    {children}
    {hint ? <small className="dashboard-field-hint">{hint}</small> : null}
  </label>
);

const MerchantBusinessForm = ({ initialData, userProfile }) => {
  const { user, token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(EMPTY_BUSINESS_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setFormData(merchantToForm(initialData, userProfile || user));
  }, [initialData, userProfile, user]);

  const mutation = useMutation(
    async (payload) => {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/users/update/merchants/${user.userId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || "Failed to save business information");
      }

      return response.json();
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || "Business information saved");
        queryClient.invalidateQueries("businessMerchant");
        if (data.merchant) {
          setFormData(merchantToForm(data.merchant, userProfile || user));
        }
        setSubmitting(false);
      },
      onError: (error) => {
        toast.error(error.message || "Could not save business information");
        setSubmitting(false);
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    mutation.mutate(buildBusinessSavePayload(formData));
  };

  return (
    <form onSubmit={handleSubmit} className="dashboard-business-form">
      <section className="dashboard-form-section">
        <h3 className="dashboard-form-section__title">Basic details</h3>
        <div className="dashboard-form-grid">
          <Field label="Business name *" count={formData.businessName} max={FIELD_LIMITS.short} full>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              maxLength={FIELD_LIMITS.short}
              required
            />
          </Field>

          <Field label="Business type">
            <select name="businessType" value={formData.businessType} onChange={handleChange}>
              <option value="">Select type</option>
              {BUSINESS_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Category">
            <select name="businessCategory" value={formData.businessCategory} onChange={handleChange}>
              <option value="">Select category</option>
              {BUSINESS_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Business address" count={formData.businessAddress} max={FIELD_LIMITS.medium} full>
            <input
              type="text"
              name="businessAddress"
              value={formData.businessAddress}
              onChange={handleChange}
              maxLength={FIELD_LIMITS.medium}
              placeholder="Street, area, landmark"
            />
          </Field>

          <Field label="City">
            <input type="text" name="city" value={formData.city} onChange={handleChange} maxLength={80} />
          </Field>

          <Field label="State">
            <select name="state" value={formData.state} onChange={handleChange}>
              <option value="">Select state</option>
              {NIGERIAN_STATES.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Business description" count={formData.businessDescription} max={FIELD_LIMITS.long} full>
            <textarea
              name="businessDescription"
              value={formData.businessDescription}
              onChange={handleChange}
              rows={4}
              maxLength={FIELD_LIMITS.long}
              placeholder="What you sell and what makes your store unique"
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-form-section">
        <h3 className="dashboard-form-section__title">Contact</h3>
        <div className="dashboard-form-grid">
          <Field label="Contact person">
            <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} maxLength={FIELD_LIMITS.short} />
          </Field>
          <Field label="Contact email">
            <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleChange} maxLength={FIELD_LIMITS.short} />
          </Field>
          <Field label="Contact phone">
            <input type="tel" name="contactPhoneNumber" value={formData.contactPhoneNumber} onChange={handleChange} maxLength={20} />
          </Field>
          <Field label="Customer support info" count={formData.customerSupportInformation} max={FIELD_LIMITS.medium} full>
            <textarea
              name="customerSupportInformation"
              value={formData.customerSupportInformation}
              onChange={handleChange}
              rows={3}
              maxLength={FIELD_LIMITS.medium}
              placeholder="Hours, WhatsApp line, or support email"
            />
          </Field>
        </div>
      </section>

      <section className="dashboard-form-section">
        <h3 className="dashboard-form-section__title">Legal & tax (optional)</h3>
        <div className="dashboard-form-grid">
          <Field label="Tax ID (TIN)">
            <input type="text" name="taxIdentificationNumber" value={formData.taxIdentificationNumber} onChange={handleChange} maxLength={60} />
          </Field>
          <Field label="Business registration number">
            <input type="text" name="businessRegistrationNumber" value={formData.businessRegistrationNumber} onChange={handleChange} maxLength={60} />
          </Field>
          <Field label="Business license / permit">
            <input type="text" name="businessLicenseOrPermit" value={formData.businessLicenseOrPermit} onChange={handleChange} maxLength={120} />
          </Field>
          <Field label="VAT registration">
            <input type="text" name="valueAddedTaxRegistration" value={formData.valueAddedTaxRegistration} onChange={handleChange} maxLength={60} />
          </Field>
          <Field label="GST registration">
            <input type="text" name="goodsAndServicesTaxRegistration" value={formData.goodsAndServicesTaxRegistration} onChange={handleChange} maxLength={60} />
          </Field>
          <Field label="Customs ID">
            <input type="text" name="customsIdentificationNumber" value={formData.customsIdentificationNumber} onChange={handleChange} maxLength={60} />
          </Field>
        </div>
      </section>

      <section className="dashboard-form-section">
        <h3 className="dashboard-form-section__title">Store policies</h3>
        <div className="dashboard-form-grid">
          <Field label="Product information" count={formData.productInformation} max={FIELD_LIMITS.medium} full>
            <textarea name="productInformation" value={formData.productInformation} onChange={handleChange} rows={3} maxLength={FIELD_LIMITS.medium} />
          </Field>
          <Field label="Returns & refunds policy" count={formData.returnsAndRefundsPolicy} max={FIELD_LIMITS.medium} full>
            <textarea name="returnsAndRefundsPolicy" value={formData.returnsAndRefundsPolicy} onChange={handleChange} rows={3} maxLength={FIELD_LIMITS.medium} />
          </Field>
        </div>
      </section>

      <div className="dashboard-form-actions">
        <button type="submit" className="dashboard-btn dashboard-btn--primary" disabled={submitting}>
          {submitting ? "Saving…" : "Save business information"}
        </button>
      </div>
    </form>
  );
};

export default MerchantBusinessForm;
